/**
 * <s-rail> — Horizontal product strip with previous/next buttons.
 *
 * Native scrolling (swipe, trackpad, keyboard) does the work; the buttons scroll by
 * roughly one viewport of the track and disable themselves at either end.
 *
 * Required child markup:
 *   [data-rail-track] — The overflow-x scroller.
 *   [data-rail-prev], [data-rail-next] — Buttons.
 */
class SRail extends HTMLElement {
  #ac;
  #track;
  #prev;
  #next;

  connectedCallback() {
    this.#track = this.querySelector('[data-rail-track]');
    this.#prev = this.querySelector('[data-rail-prev]');
    this.#next = this.querySelector('[data-rail-next]');
    if (!this.#track) return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#prev?.addEventListener('click', () => this.#step(-1), { signal });
    this.#next?.addEventListener('click', () => this.#step(1), { signal });
    this.#track.addEventListener('scroll', this.#update, { passive: true, signal });
    window.addEventListener('resize', this.#update, { passive: true, signal });
    this.#update();
    this.#startDrive(signal);
  }


  /* Scroll-driven glide (opt-in with the data-scroll-drive attribute on <s-rail>).
     While the rail crosses the viewport, page scroll eases the track sideways from start to end.
     Any direct interaction (pointer, wheel, keys, buttons) hands control back for good.
     Skipped entirely for reduced-motion users. */
  #driving = false;
  #raf = 0;
  #current = 0;

  #startDrive(signal) {
    if (!this.hasAttribute('data-scroll-drive')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce), (max-width: 767px), (hover: none)').matches) return;
    this.#driving = true;
    this.#track.style.scrollSnapType = 'none';
    this.#current = Math.abs(this.#track.scrollLeft);
    const stop = () => this.#stopDrive();
    this.#track.addEventListener('wheel', (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) stop(); }, { passive: true, signal });
    this.#track.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'touch') stop(); }, { passive: true, signal });
    let touchX = 0, touchY = 0;
    this.#track.addEventListener('touchstart', (e) => { touchX = e.touches[0].clientX; touchY = e.touches[0].clientY; }, { passive: true, signal });
    this.#track.addEventListener('touchmove', (e) => { const t = e.touches[0]; if (Math.abs(t.clientX - touchX) > Math.abs(t.clientY - touchY) + 4) stop(); }, { passive: true, signal });
    for (const type of ['keydown']) {
      this.#track.addEventListener(type, stop, { passive: true, signal });
    }
    this.#prev?.addEventListener('click', stop, { signal });
    this.#next?.addEventListener('click', stop, { signal });
    window.addEventListener('scroll', this.#queue, { passive: true, signal });
    window.addEventListener('resize', this.#queue, { passive: true, signal });
    this.#queue();
  }

  #stopDrive() {
    if (!this.#driving) return;
    this.#driving = false;
    cancelAnimationFrame(this.#raf);
    this.#track.style.scrollSnapType = '';
  }

  #queue = () => {
    if (!this.#driving || this.#raf) return;
    this.#raf = requestAnimationFrame(this.#tick);
  };

  #tick = () => {
    this.#raf = 0;
    if (!this.#driving) return;
    const track = this.#track;
    const max = track.scrollWidth - track.clientWidth;
    if (max <= 2) return;
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight;
    const progress = Math.min(1, Math.max(0, (vh - rect.top) / (vh + rect.height)));
    const reach = Math.min(max, Number(this.getAttribute('data-scroll-drive')) || 96);
    const target = progress * reach;
    this.#current += (target - this.#current) * 0.06;
    if (Math.abs(target - this.#current) < 0.5) this.#current = target;
    track.scrollLeft = this.#isRtl() ? -this.#current : this.#current;
    if (this.#current !== target) this.#queue();
  };

  disconnectedCallback() {
    this.#stopDrive();
    this.#ac?.abort();
  }

  #isRtl() {
    return getComputedStyle(this.#track).direction === 'rtl';
  }

  #step(direction) {
    const distance = Math.max(this.#track.clientWidth * 0.8, 200);
    const sign = this.#isRtl() ? -1 : 1;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce), (max-width: 767px), (hover: none)').matches;
    this.#track.scrollBy({ left: distance * direction * sign, behavior: reduce ? 'auto' : 'smooth' });
  }

  #update = () => {
    const track = this.#track;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const atStart = position <= 2;
    const atEnd = position >= max - 2;
    if (this.#prev) this.#prev.disabled = atStart;
    if (this.#next) this.#next.disabled = atEnd;
    this.toggleAttribute('data-scrollable', max > 2);
    this.toggleAttribute('data-at-end', atEnd);
  };
}

if (!customElements.get('s-rail')) customElements.define('s-rail', SRail);
